import { useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { ApiContext } from '@api/api.tsx';
import { FeaturesEnum, RampOpeningHoursByDateRangeOutDTO, ReservationStatisticsResponseOutDTO } from '@api/ringil-api.v2';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { useLocalStorage } from '@utils/utils';
import { CalendarViewType, daysInView } from './calendarView';

interface Params {
  currentDate: string;
  currentView: CalendarViewType;
  ramps: RampOpeningHoursByDateRangeOutDTO[];
}

/** Aggregated per-day statistics shown above the grid; gated by the TIMESLOT_STATISTICS feature. */
export const useCalendarStats = ({ currentDate, currentView, ramps }: Params) => {
  const { getReservationStatisticsForDay } = useContext(ApiContext);
  const { hasFeature } = useContext(AuthenticatedUserContext);
  const statisticsEnabled = hasFeature([FeaturesEnum.TIMESLOT_STATISTICS]);

  const [stats, setStats] = useState<ReservationStatisticsResponseOutDTO | null>(null);
  const [statsOpened, setStatsOpened] = useLocalStorage('TIMESLOT_CALENDAR_STATS_OPENED', false);
  const [statsActive, setStatsActive] = useLocalStorage('TIMESLOT_CALENDAR_STATS_SETTINGS', [true, true, true]);

  useEffect(() => {
    // Skip statistics for users without the feature (e.g. carriers viewing inherited customer calendars).
    if (!statisticsEnabled) return;
    getReservationStatisticsForDay(
      {
        fromDay: dayjs(currentDate).format('YYYY-MM-DD'),
        futureDay: daysInView(currentView) - 1,
        rampIds: ramps.map(r => r.rampId),
      },
      data => setStats(data),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, currentView, ramps, statisticsEnabled]);

  return { statisticsEnabled, stats, statsOpened, setStatsOpened, statsActive, setStatsActive };
};